import axios from "axios";
import { DateTime } from "luxon";
import process from "prop-types/prop-types";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";

import Breadcrumbs from "../../component/breadcrumbs";
import CustomHelmet from "../../component/CustomHelmet";
import FullScreenLoader from "../../component/FullScreenLoader";
import Intro from "../../component/Intro";
import BaseContainer from "../../component/layout/base-container";
import { SlideDown } from "../../component/SlideDown";
import customStyles from "../../customStyles/dashboardHome.module.scss";
import { getToken } from "../../lib/authentication";
import DeleteModal from "../../lib/deleteModal";
import EditModal from "../../lib/editModal";
import { auth_fetcher, custom_fetcher } from "../../lib/fetcher";
import { request_methods } from "../../lib/request_methods";

function ProgressCell({ surveyRun, progressType }) {
  return (
    <span>
      {surveyRun["survey_progress"][progressType]["done"]} /{" "}
      {surveyRun["survey_progress"][progressType]["total"]}
    </span>
  );
}

function SortArrow({ keyword, currentState, callback }) {
  const currentStateMatch = currentState.endsWith(keyword);
  return (
    <button
      onClick={() => {
        if (currentStateMatch) {
          if (currentState.startsWith("-")) {
            callback(keyword);
          } else {
            callback(`-${keyword}`);
          }
        } else {
          callback(keyword);
        }
      }}
    >
      <span
        style={{
          color:
            currentStateMatch && !currentState.startsWith("-")
              ? "inherit"
              : "grey",
        }}
      >
        ↑
      </span>
      <span
        style={{
          color:
            currentStateMatch && currentState.startsWith("-")
              ? "inherit"
              : "grey",
        }}
      >
        ↓
      </span>
    </button>
  );
}

export default function DashboardHome({ user }) {
  const [page, setPage] = useState(1);
  const [ordering, setOrdering] = useState("");

  const {
    data,
    error,
    mutate: surveyruns_mutate,
  } = useSWR(
    `${process.env.REACT_APP_BASE_URL}surveyruns/?page=${page}&ordering=${ordering}`,
    auth_fetcher,
    {
      refreshInterval: 1000,
    }
  );

  const { data: survey_run_options } = useSWR(
    [`${process.env.REACT_APP_BASE_URL}surveyruns/`, request_methods.options],
    custom_fetcher
  );

  const [deleteDialog, setDeleteDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);

  const klappen_texte = [
    {
      title: "Wie werden Umfragen erstellt?",
      text: (
        <>
          <p>Über «Neue Umfrage erstellen» können Sie eine Umfrage anlegen.</p>
          <p>
            Die Konfiguration der Umfrage liegt in Ihrer Hand. Sie entscheiden,
            welche Personengruppen und wie viele Personen neben Ihren eigenen
            Einschätzungen einbezogen werden sollen. FormATool ermöglicht Ihnen
            ebenfalls, mehrere Umfragen zu erstellen, d.h. Sie können mehrere
            Klassen einbeziehen oder bei derselben Klasse mehrmalig Umfragen
            durchführen.
          </p>
        </>
      ),
    },
    {
      title: "Wie werden Umfragen durchgeführt?",
      text: (
        <>
          <p>
            Nach Erstellung der Umfrage können automatisch generierte
            Zugangscodes für Lehrpersonen, Beobachter*innen und Schüler*innen
            abgerufen werden. Mit diesen Zugangscodes wird eine anonymisierte
            Teilnahme gewährleistet. Die Anzahl der Zugangscodes entspricht der
            angegebenen Personenanzahl in der erstellten Umfrage. Für die
            Teilnahme an der Umfrage können Computer, Tablets oder Smartphones
            mit Internetzugang verwendet werden.
          </p>
          <p>
            Die Selbsteinschätzungen der Lehrpersonen zur Unterrichtslektion
            sollten möglichst zeitnah nach der betreffenden Lektion erfolgen, so
            sind die Erinnerungen am frischesten und nicht durch nachfolgende
            Unterrichtslektionen oder -tage getrübt.
          </p>
          <p>
            Die Einschätzungen der Schüler*innen müssen von den Lehrpersonen
            eingeholt werden. Die Zugangscodes lassen sich zu diesem Zweck
            ausdrucken, sodass Lehrpersonen den Schüler*innen die Zugangscodes
            aushändigen können. Die Schüler*innen können dann auf zwei Wegen zur
            Umfrage gelangen: Entweder sie benutzen die automatisch generierten
            QR-Codes, die in der Druckansicht integriert sind und die sie direkt
            zur Umfrage führen oder sie geben den Zugangscode auf der
            FormATool-Webseite ein und gelangen so zur Umfrage. Wir empfehlen,
            die Umfrage gleich im Anschluss an die betreffende
            Unterrichtslektion ausfüllen zu lassen. Je nach Geübtheit und Alter
            der Schüler*innen mit technischen Hilfsmitteln muss mit 5-10 Minuten
            Bearbeitungszeit für die Umfrage gerechnet werden.
          </p>
          <p>
            Die Einschätzungen der Beobachter*innen müssen ebenfalls von der
            Lehrperson eingeholt werden. Die Zugangscodes inkl. QR-Codes lassen
            sich zu diesem Zweck ausdrucken. Auch die Beobachter*innen können
            auf zwei Wegen zur Umfrage gelangen: Entweder sie benutzen die
            automatisch generierten QR-Codes, die in der Druckansicht integriert
            sind und die sie direkt zur Umfrage führen oder sie geben den
            Zugangscode auf der FormATool-Webseite ein und gelangen so zur
            Umfrage.
          </p>
          <p>
            Sobald alle Personen die Fragen beantwortet haben, kann die
            Lehrperson in Ihrer Umfrageübersicht die Umfrage beenden und auf die
            automatisch generierten Auswertungen einsehen.
          </p>
        </>
      ),
    },
    {
      title: "Wie werden Umfragen ausgewertet?",
      text: (
        <>
          <p>
            Nachdem alle Beteiligten die Umfrage ausgefüllt haben und die
            Lehrperson die Umfrage beendet, wird automatisch eine Auswertung
            ausgegeben, auf der die Einschätzungen der verschiedenen
            Perspektiven auf unterschiedliche Weise dargestellt werden. Zudem
            werden Hinweise zur Interpretation der Ergebnisse zur Verfügung
            gestellt. Auf diese Weise sollen Entwicklungsbereiche für die
            Unterrichtsentwicklung sichtbar gemacht werden. Diese Informationen
            sind ausschliesslich für die betreffende Lehrperson zugänglich,
            Dritte wie beispielsweise die Schulleitung haben keine Kenntnis über
            die Daten.
          </p>
        </>
      ),
    },
  ];

  if (error) return <div>failed to load</div>;
  if (!data || !survey_run_options) return <FullScreenLoader />;

  const surveys = data.results;

  return (
    <>
      <CustomHelmet pageTitle="Dashboard" />
      <Breadcrumbs
        listItems={[
          { name: "Home", url: "/" },
          { name: "Umfragen", url: "/dashboard" },
        ]}
      />
      <BaseContainer>
        <Intro title="Umfrageübersicht" />
      </BaseContainer>
      <BaseContainer oversize className={customStyles.dashboardHome}>
        <div className="flex flex-col">
          <p>Meine Umfragen:</p>
          <div>
            <div className="DatabaseQuery--table">
              <table className="ornate relative">
                <thead>
                  <tr className="whitespace-nowrap">
                    <th className="!pl-0">
                      Beschreibung&nbsp;
                      <SortArrow
                        keyword="description_sort"
                        currentState={ordering}
                        callback={setOrdering}
                      />
                    </th>
                    <th>
                      Datum&nbsp;
                      <SortArrow
                        keyword="date"
                        currentState={ordering}
                        callback={setOrdering}
                      />
                    </th>
                    <th>Schulart</th>
                    <th>Klassenstufe(n)</th>
                    <th>
                      Fach&nbsp;
                      <SortArrow
                        keyword="subject"
                        currentState={ordering}
                        callback={setOrdering}
                      />
                    </th>
                    <th>Fortschritt Umfragen</th>
                  </tr>
                </thead>
                <tbody>
                  {surveys.length > 0 ? (
                    surveys.map((survey_run) => (
                      <tr key={survey_run["url"]}>
                        <td>{survey_run["description"]}</td>
                        <td>
                          {DateTime.fromISO(survey_run["date"])
                            .setLocale("de-CH")
                            .toLocaleString(DateTime.DATE_MED)}
                        </td>
                        <td>{survey_run["school_type"]}</td>
                        <td>{survey_run["school_step"].join(", ")}</td>
                        <td>{survey_run["subject"]}</td>
                        <td className={customStyles.progress}>
                          Lehrpersonen:{" "}
                          <ProgressCell
                            progressType="L"
                            surveyRun={survey_run}
                          />
                          <br />
                          Beobachter*innen:{" "}
                          <ProgressCell
                            progressType="E"
                            surveyRun={survey_run}
                          />
                          <br />
                          Schüler*innen:{" "}
                          <ProgressCell
                            progressType="S"
                            surveyRun={survey_run}
                          />
                        </td>
                        <td>
                          <ul className="LinkList--list">
                            <li>
                              {survey_run["done"] ? (
                                <Link
                                  to={`auswertung/${survey_run["id"]}`}
                                  className="Link"
                                >
                                  <span className="whitespace-nowrap">
                                    Auswertung anzeigen
                                  </span>
                                </Link>
                              ) : (
                                <Link
                                  to={`teilnahmeliste/${survey_run["id"]}`}
                                  className="Link"
                                >
                                  <span className="whitespace-nowrap">
                                    Zugangscodes anzeigen
                                  </span>
                                </Link>
                              )}
                            </li>
                            <li>
                              {survey_run["done"] ? (
                                <button
                                  className="Link"
                                  onClick={() =>
                                    axios
                                      .patch(
                                        survey_run["url"],
                                        { done: false },
                                        {
                                          headers: {
                                            Authorization: `Token ${getToken()}`,
                                          },
                                        }
                                      )
                                      .then(() => surveyruns_mutate())
                                  }
                                >
                                  Umfrage öffnen
                                </button>
                              ) : (
                                <button
                                  className="Link"
                                  onClick={() =>
                                    axios
                                      .patch(
                                        survey_run["url"],
                                        { done: true },
                                        {
                                          headers: {
                                            Authorization: `Token ${getToken()}`,
                                          },
                                        }
                                      )
                                      .then(() => surveyruns_mutate())
                                  }
                                >
                                  Umfrage beenden
                                </button>
                              )}
                            </li>
                            <li>
                              <button
                                className="Link"
                                onClick={() => setEditDialog(survey_run)}
                              >
                                Umfrage bearbeiten
                              </button>
                            </li>
                            <li>
                              <button
                                className="Link"
                                onClick={() => setDeleteDialog(survey_run)}
                              >
                                Umfrage löschen
                              </button>
                            </li>
                            {!survey_run.done &&
                              !survey_run.individual_surveys?.find(
                                (s) => s.type === "L"
                              ).done && (
                                <li>
                                  <Link
                                    to={`/umfrage/${
                                      survey_run.individual_surveys?.find(
                                        (s) => s.type === "L"
                                      ).tan
                                    }`}
                                    className="Link"
                                  >
                                    <span className="whitespace-nowrap">
                                      Umfrage ausfüllen
                                    </span>
                                  </Link>
                                </li>
                              )}
                          </ul>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} style={{ textAlign: "center" }}>
                        Du hast noch keine Umfragen.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {data["next"] || data["previous"] ? (
                <nav className="Pagination" aria-label="Pagination">
                  <div className="Pagination--body">
                    <button
                      className="Pagination--button prev"
                      type="button"
                      disabled={!data["previous"]}
                      onClick={() => setPage(data["previous"])}
                    >
                      <span className="visuallyhidden">Vorherige Seite</span>
                    </button>
                    <div className="Pagination--current">
                      <span className="js-Pagination--current">{page}</span> von{" "}
                      <span className="js-Pagination--max">
                        {data["total_pages"]}
                      </span>
                    </div>
                    <button
                      className="Pagination--button next"
                      type="button"
                      disabled={!data["next"]}
                      onClick={() => setPage(data["next"])}
                    >
                      <span className="visuallyhidden">Nächste Seite</span>
                    </button>
                  </div>
                </nav>
              ) : (
                ""
              )}
            </div>
          </div>
          <div
            className="FormButtons my-[56px]"
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            {user.is_staff ? (
              <a
                className="Button color-primary size-large"
                href={`${process.env.REACT_APP_BASE_URL}export`}
                target="_blank"
                rel="noreferrer"
              >
                Export
              </a>
            ) : (
              ""
            )}
            <Link className="Button color-primary size-large" to="neue-umfrage">
              Neue Umfrage erstellen
            </Link>
          </div>
        </div>
      </BaseContainer>
      <BaseContainer>
        <div className="NewsList">
          <p>
            Hier finden Sie alle Informationen zur Erstellung und Durchführung
            von Umfragen mit FormATool auf einem Blick:
          </p>
          <ul className="NewsList--list">
            {klappen_texte.map((element) => (
              <li key={element.title}>
                <SlideDown title={element.title} content={element.text} />
              </li>
            ))}
          </ul>
        </div>
      </BaseContainer>
      <DeleteModal
        deleteDialog={deleteDialog}
        setDeleteDialog={setDeleteDialog}
        surveyruns_mutate={surveyruns_mutate}
      />
      <EditModal
        editDialog={editDialog}
        setEditDialog={setEditDialog}
        surveyruns_mutate={surveyruns_mutate}
      />
    </>
  );
}
